import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
/**
 * Pointer hover timings.
 *
 * OPEN_DELAY buys hover intent. Without it, sweeping the pointer across the
 * header on the way to the cart fires activate on every menu item it crosses, so
 * panels flash open and shut and the header jumps as --submenu-height animates
 * to each one in turn. Skipped when a panel is already open: moving along an
 * open menu bar should switch instantly.
 *
 * CLOSE_DELAY is the grace period for crossing the gap between a menu item and
 * its panel. deactivate() already tries to detect that move via relatedTarget,
 * but relatedTarget is whatever sits under the cursor at that instant, and on a
 * quick diagonal that is usually the header background rather than the submenu,
 * so the guard misses and the panel shuts under the pointer.
 */
const OPEN_DELAY = 120;
const CLOSE_DELAY = 320;   // 220 felt clipped crossing a wide centred row

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #openTimer = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimer = null;

  /** @type {PointerEvent | FocusEvent | null} */
  #pendingEvent = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    // Closing is owned by the menu as a whole, not by individual items. See
    // #menuLeave.
    this.addEventListener('pointerleave', this.#menuLeave);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointerleave', this.#menuLeave);
    this.#clearTimers();
    this.#cleanupMutationObserver();
  }

  /**
   * The panel only closes when the pointer leaves the whole menu.
   *
   * Each top-level <li> is about 40px wide while the panel it owns spans the
   * page, so moving diagonally from a heading out into its own panel crosses
   * sibling <li> boxes on the way. Per-item pointerleave therefore fired a close
   * mid-gesture, and whether the panel survived came down to timing. Hoisting
   * the close to the component makes the whole header one hover region: items
   * still switch on pointerenter, but nothing closes until the pointer is
   * genuinely out.
   */
  #menuLeave = (event) => {
    // <header-menu> does not cover the whole header. There is a band of header
    // chrome between the menu bar and an open panel, so a diagonal from a
    // heading to a link in its own panel leaves this element and re-enters it,
    // and on a long traverse the close fired in between. If the pointer is
    // still somewhere in the header, it has not left the menu in any sense the
    // customer would recognise.
    const to = event?.relatedTarget;
    if (to instanceof Node && this.headerComponent?.contains(to)) return;

    if (this.#openTimer) {
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
      this.#pendingEvent = null;
    }
    const item = this.#state.activeItem;
    if (!item) return;
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      this.#deactivate(item);
    }, CLOSE_DELAY);
  };

  #clearTimers() {
    if (this.#openTimer) clearTimeout(this.#openTimer);
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#openTimer = null;
    this.#closeTimer = null;
    this.#pendingEvent = null;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Entry point bound in Liquid via on:pointerenter and on:focus.
   *
   * Keyboard focus always acts immediately — a user tabbing through the header
   * should never wait. Only pointer hover is put through hover intent.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Any incoming interaction cancels a pending close.
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    const isPointer = event.type === 'pointerenter';
    const somethingOpen = Boolean(this.#state.activeItem);

    // A childless top-level item changes nothing while a panel is open.
    //
    // Discover sits between Shop and Our Store and its panel is a centred row,
    // so the natural move from the Discover heading to its first link travels
    // right across Our Store, which has no submenu. Switching to it collapsed
    // the menu mid-gesture. A timed close does not fix this either: that
    // traverse takes longer than any grace period worth having, so the panel
    // still died before the pointer arrived.
    //
    // So: do nothing. Closing is already owned by #menuLeave, which fires when
    // the pointer leaves the header entirely. Hovering Our Store with another
    // panel open leaves that panel up, which is the right trade — Our Store has
    // nothing of its own to show, and the alternative is a menu that collapses
    // while you are reaching for it.
    if (isPointer && somethingOpen && !findSubmenu(findMenuItem(event.target))) return;

    if (!isPointer || somethingOpen) {
      this.#activateNow(event);
      return;
    }

    // Cancellation, not a :hover re-check. The pointerleave handler clears this
    // timer, so a timer that survives the delay means the pointer never left.
    // Re-checking :hover instead would be both redundant and untestable, since
    // a synthetic pointer event leaves nothing actually hovered.
    if (this.#openTimer) clearTimeout(this.#openTimer);
    this.#pendingEvent = event;
    this.#openTimer = setTimeout(() => {
      this.#openTimer = null;
      const pending = this.#pendingEvent;
      this.#pendingEvent = null;
      if (pending) this.#activateNow(pending);
    }, OPEN_DELAY);
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  #activateNow = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      // Only #deactivate used to clear this, which never runs on a switch, so
      // every panel hovered in a session stayed flagged active and kept its
      // content-visibility optimisation defeated.
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              // LDFG: boxed dropdowns never grow the header band
              const h = isDefaultSlot ? 0 : submenu.offsetHeight;
              this.headerComponent?.style.setProperty('--submenu-height', `${h}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // LDFG 2026-08-24: top-level dropdowns are floating boxed cards (see
    // blocks/_header-menu.liquid), so the header band must NOT expand for
    // them. The overflow "More" menu keeps Horizon's expanding behaviour.
    if (isDefaultSlot) {
      finalHeight = 0;
    }

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // Leaving before hover intent fired means nothing should open at all.
    if (this.#openTimer) {
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
      this.#pendingEvent = null;
    }

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    // Leaving one item for anywhere else inside the menu is not a close: either
    // a sibling is about to activate, or the pointer is on its way into the open
    // panel. #menuLeave owns closing for the pointer.
    if (event.type === 'pointerleave') return;

    // Blur is a keyboard user leaving, so close at once.
    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
